import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessUpdateAlert, showError } from '../../../../core/alert/sweetAlertConfig';

interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface UpdateFloorModuleProps {
  show: boolean;
  onHide: () => void;
  floor: FloorDTO | null;
  onSuccess: () => void;
}

const UpdateFloorModule: React.FC<UpdateFloorModuleProps> = ({
  show,
  onHide,
  floor,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    floorNumber: '',
    buildingId: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize formData when floor prop changes
  useEffect(() => {
    if (floor) {
      setFormData({
        id: floor.id.toString(),
        name: floor.name,
        floorNumber: floor.floorNumber.toString(),
        buildingId: floor.buildingId.toString(),
      });
    }
  }, [floor]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!floor) {
      showError('No floor data available. Please try again.');
      onHide();
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        id: parseInt(formData.id),
        name: formData.name.trim(),
        floorNumber: parseInt(formData.floorNumber),
        buildingId: parseInt(formData.buildingId),
      };
      const response = await apiDefinitions.updateFloor(payload);
      if (response.status === 200) {
        showSuccessUpdateAlert("Floor updated successfully");
        onSuccess();
        onHide();
      } else {
        showError(response.data?.statusDescription || 'Failed to update floor');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
      } else if (err.response?.status === 400) {
        showError(err.response.data?.statusDescription || 'Invalid floor data');
      } else if (err.response?.status === 409) {
        showError('Floor name already exists in this building');
      } else {
        showError('Failed to update floor. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!floor) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Floor: {floor.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="floorId">
            <Form.Label>ID</Form.Label>
            <Form.Control
              type="text"
              name="id"
              value={formData.id}
              disabled
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="floorName">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter floor name"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="floorNumber">
            <Form.Label>Floor Number</Form.Label>
            <Form.Control
              type="number"
              name="floorNumber"
              value={formData.floorNumber}
              onChange={handleInputChange}
              placeholder="Enter floor number"
              min="0"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="buildingName">
            <Form.Label>Building</Form.Label>
            <Form.Control
              type="text"
              value={floor.buildingName}
              disabled
            />
            <Form.Control
              type="hidden"
              name="buildingId"
              value={formData.buildingId}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="locationName">
            <Form.Label>Location</Form.Label>
            <Form.Control
              type="text"
              value={floor.locationName}
              disabled
            />
          </Form.Group>
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={onHide}
              disabled={isLoading}
              className="me-2"
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.floorNumber}
            >
              {isLoading ? 'Updating...' : 'Update Floor'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default UpdateFloorModule;